// @ts-check
// Editor de blocos no celular (spec/email.md seção 19.2, até 768 px): a lista vertical das seções e dos blocos em
// miniatura; tocar abre as propriedades numa folha inferior; adicionar pelo "+" e mover pelos botões, sem arrastar.
import { html } from 'htm/preact';
import { EMAIL_FONT_STACKS } from '../../../../../shared/email-design/email-document.js';
import { findBlock } from '../../../../../shared/email-design/document-operations.js';
import { Button, LightIconButton } from '../../../ui/button.js';
import { Icon } from '../../../ui/icon.js';
import { useState } from 'preact/hooks';
import { BlockContent, canvasImageUrl } from './block-canvas.js';
import { PropertiesPanel } from './block-properties.js';
import { blockPaletteKey, blockSummary, findPaletteItem } from './block-catalog.js';
import { t } from '../../../i18n/index.js';

/**
 * @typedef {import('./block-canvas.js').CanvasEditor} CanvasEditor
 * @typedef {import('./block-canvas.js').DropTarget} DropTarget
 * @typedef {import('../../../../../shared/email-design/email-document.js').EmailBlock} EmailBlock
 */

/**
 * Miniatura de um bloco na lista.
 * @param {{ block: EmailBlock, editor: CanvasEditor }} props
 */
function BlockThumbnail({ block, editor }) {
  if (block.type === 'IMAGE') {
    const url = canvasImageUrl(block, editor);
    if (url) return html`<img class="email-mobile-thumbnail-image" src=${url} alt="" />`;
  }
  const key = blockPaletteKey(block);
  return html`<span class="email-mobile-thumbnail-icon"><${Icon} name=${findPaletteItem(key)?.icon ?? 'IconBox'} size=${18} /></span>`;
}

/**
 * Folha inferior com o item selecionado.
 * @param {{ title: string, onClose: () => void, actions?: any, children: any }} props
 */
function BottomSheet({ title, onClose, actions, children }) {
  return html`<div class="email-bottom-sheet-backdrop" onMouseDown=${(/** @type {MouseEvent} */ event) => event.target === event.currentTarget && onClose()}>
    <section class="email-bottom-sheet" role="dialog" aria-modal="true" aria-label=${title}>
      <header class="email-bottom-sheet-header">
        <span class="email-bottom-sheet-title">${title}</span>
        <${LightIconButton} icon="IconX" size="medium" ariaLabel=${t('common.close')} onClick=${onClose} />
      </header>
      ${actions ? html`<div class="email-bottom-sheet-actions">${actions}</div>` : null}
      <div class="email-bottom-sheet-body">${children}</div>
    </section>
  </div>`;
}

/**
 * @param {{ canvasEditor: CanvasEditor, propertiesEditor: any, stylePanel: any, onAdd: (anchor: HTMLElement, target: DropTarget) => void }} props
 */
export function BlockMobileList({ canvasEditor: editor, propertiesEditor, stylePanel, onAdd }) {
  const [isStyleOpen, setIsStyleOpen] = useState(false);
  const sections = editor.document.sections ?? [];
  const selection = editor.selection;
  const style = editor.document.style;
  const selectedBlock = selection?.kind === 'BLOCK' ? findBlock(editor.document, selection.id) : null;
  const selectedSectionIndex = selection?.kind === 'SECTION' ? sections.findIndex((section) => section.id === selection.id) : -1;

  /** @param {'UP' | 'DOWN' | 'DUPLICATE' | 'DELETE'} action */
  const itemActions = (action) => {
    if (selection?.kind === 'BLOCK') editor.blockAction(action, selection.id);
    else if (selection?.kind === 'SECTION') editor.sectionAction(action, selection.id);
  };
  const canMoveUp = selection?.kind === 'BLOCK' ? editor.canMoveBlock(selection.id, -1) : selectedSectionIndex > 0;
  const canMoveDown = selection?.kind === 'BLOCK' ? editor.canMoveBlock(selection.id, 1) : selectedSectionIndex >= 0 && selectedSectionIndex < sections.length - 1;
  const sheetActions = editor.readOnly || selection?.kind === 'COLUMN'
    ? null
    : html`
        <${LightIconButton} icon="IconArrowUp" size="medium" ariaLabel=${t('emailDesign.toolbar.moveUp')} disabled=${!canMoveUp} onClick=${() => itemActions('UP')} />
        <${LightIconButton} icon="IconArrowDown" size="medium" ariaLabel=${t('emailDesign.toolbar.moveDown')} disabled=${!canMoveDown} onClick=${() => itemActions('DOWN')} />
        <${LightIconButton} icon="IconCopy" size="medium" ariaLabel=${t('emailDesign.toolbar.duplicate')} onClick=${() => itemActions('DUPLICATE')} />
        <${LightIconButton} icon="IconTrash" size="medium" ariaLabel=${t('emailDesign.toolbar.delete')} onClick=${() => itemActions('DELETE')} />
      `;
  const sheetTitle = selectedBlock
    ? t(`emailDesign.palette.${blockPaletteKey(selectedBlock)}`)
    : selection?.kind === 'SECTION'
      ? t('emailDesign.sectionLabel', { position: selectedSectionIndex + 1 })
      : selection?.kind === 'COLUMN'
        ? t('emailDesign.properties.column', { position: selection.columnIndex + 1 })
        : '';

  return html`<div class="email-mobile-list">
    <p class="email-mobile-hint"><${Icon} name="IconDeviceDesktop" size=${14} />${t('emailTemplates.editor.mobileBlocksHint')}</p>
    ${editor.readOnly ? null : html`<div class="email-mobile-toolbar">
      <${Button} title=${t('emailDesign.palette.style')} icon="IconPalette" size="small" variant="secondary" onClick=${() => setIsStyleOpen(true)} />
    </div>`}
    ${sections.length === 0 ? html`<p class="email-mobile-empty">${editor.readOnly ? t('emailDesign.emptyReadOnly') : t('emailDesign.emptyCanvas')}</p>` : null}
    <ol class="email-mobile-sections">
      ${sections.map((section, sectionIndex) => html`<li key=${section.id} class="email-mobile-section" data-selected=${selection?.kind === 'SECTION' && selection.id === section.id ? 'true' : undefined}>
        <button type="button" class="email-mobile-section-header" onClick=${() => editor.select({ kind: 'SECTION', id: section.id })}>
          <${Icon} name="IconLayoutRows" size=${14} />
          <span>${t('emailDesign.sectionLabel', { position: sectionIndex + 1 })}</span>
          <span class="email-mobile-section-layout">${t(`emailDesign.layouts.${section.layout}`)}</span>
        </button>
        ${section.columns.map((column, columnIndex) => html`<div key=${column.id} class="email-mobile-column">
          ${section.columns.length > 1 ? html`<button type="button" class="email-mobile-column-label" onClick=${() => editor.select({ kind: 'COLUMN', sectionId: section.id, columnIndex })}>
            ${t('emailDesign.properties.column', { position: columnIndex + 1 })}
          </button>` : null}
          <ul class="email-mobile-blocks">
            ${column.blocks.map((block) => html`<li key=${block.id}>
              <button type="button" class="email-mobile-block" data-block-id=${block.id}
                data-selected=${selection?.kind === 'BLOCK' && selection.id === block.id ? 'true' : undefined}
                data-error=${editor.problemsByBlock.get(block.id)?.errors.length ? 'true' : undefined}
                onClick=${() => editor.select({ kind: 'BLOCK', id: block.id })}>
                <${BlockThumbnail} block=${block} editor=${editor} />
                <span class="email-mobile-block-texts">
                  <span class="email-mobile-block-type">${t(`emailDesign.palette.${blockPaletteKey(block)}`)}</span>
                  <span class="email-mobile-block-summary">${blockSummary(block)}</span>
                </span>
                ${editor.problemsByBlock.get(block.id)?.errors.length ? html`<${Icon} name="IconAlertTriangle" size=${14} className="email-mobile-block-alert" />` : null}
              </button>
            </li>`)}
          </ul>
          ${editor.readOnly ? null : html`<button type="button" class="email-mobile-add"
            onClick=${(/** @type {MouseEvent} */ event) => onAdd(/** @type {HTMLElement} */ (event.currentTarget), { kind: 'BLOCK', target: { sectionId: section.id, columnIndex, index: column.blocks.length } })}>
            <${Icon} name="IconPlus" size=${14} />${t('emailDesign.toolbar.add')}
          </button>`}
        </div>`)}
      </li>`)}
    </ol>
    ${editor.readOnly ? null : html`<button type="button" class="email-mobile-add" data-variant="section"
      onClick=${(/** @type {MouseEvent} */ event) => onAdd(/** @type {HTMLElement} */ (event.currentTarget), { kind: 'SECTION', index: sections.length })}>
      <${Icon} name="IconPlus" size=${14} />${t('emailDesign.toolbar.addSection')}
    </button>`}
    ${selection
      ? html`<${BottomSheet} title=${sheetTitle} onClose=${() => editor.select(null)} actions=${sheetActions}>
          ${selectedBlock && (selectedBlock.type === 'TEXT' || editor.readOnly)
            ? html`<div class="email-mobile-block-preview" style=${{ background: style.contentBackgroundColor, color: style.textColor, fontFamily: EMAIL_FONT_STACKS[style.fontFamily], fontSize: `${style.fontSize}px` }}>
                <${BlockContent} block=${selectedBlock} editor=${editor} columnWidth=${560} isSelected=${true} />
              </div>`
            : null}
          ${editor.readOnly ? null : html`<${PropertiesPanel} editor=${propertiesEditor} />`}
        <//>`
      : null}
    ${isStyleOpen ? html`<${BottomSheet} title=${t('emailDesign.palette.style')} onClose=${() => setIsStyleOpen(false)}>${stylePanel}<//>` : null}
  </div>`;
}
